import React from 'react';
import {
  type WebsiteAnalyticsAlert,
  WebsiteAnalyticsAlertCode,
  WebsiteAnalyticsAlertSeverity,
} from 'src/api/generated';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

/**
 * Translations of the alerts, by alert code. Partial on purpose: a code added
 * by a newer backend falls back to the backend's own text.
 */
const ALERT_TEXT_KEYS: Partial<
  Record<
    WebsiteAnalyticsAlertCode,
    { title: TranslationKey; message: TranslationKey }
  >
> = {
  [WebsiteAnalyticsAlertCode.NO_RECENT_CLICKS]: {
    title: 'websites.settings.analytics.alerts.noRecentClicks.title',
    message: 'websites.settings.analytics.alerts.noRecentClicks.message',
  },
  [WebsiteAnalyticsAlertCode.CLICK_VOLUME_DROP]: {
    title: 'websites.settings.analytics.alerts.clickVolumeDrop.title',
    message: 'websites.settings.analytics.alerts.clickVolumeDrop.message',
  },
};

/** Props for the WhatsApp analytics alerts. */
interface WebsiteAnalyticsAlertsProps {
  /** Alerts computed from recent WhatsApp clicks. */
  alerts: WebsiteAnalyticsAlert[];
}

/**
 * Lists WhatsApp analytics alerts, highlighting warnings. Known alerts are
 * shown in the user's language; unknown codes keep the backend's text.
 *
 * @param props - Alerts to show.
 * @returns The alert list, or nothing when there are no alerts.
 */
export const WebsiteAnalyticsAlerts: React.FC<WebsiteAnalyticsAlertsProps> = ({
  alerts,
}) => {
  const { t } = useI18n();

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {alerts.map((alert) => {
        const textKeys = ALERT_TEXT_KEYS[alert.code];

        return (
          <Alert
            key={alert.code}
            size="md"
            variant={
              alert.severity === WebsiteAnalyticsAlertSeverity.WARNING
                ? 'warning'
                : 'info'
            }
            message={
              <div className="space-y-1">
                <Text weight="medium">
                  {textKeys ? t(textKeys.title) : alert.title}
                </Text>
                <Text size="tiny" color="secondary">
                  {textKeys ? t(textKeys.message) : alert.message}
                </Text>
              </div>
            }
          />
        );
      })}
    </div>
  );
};
